import React, { useEffect, useState } from 'react';
import { Copy, ExternalLink, Lock, RefreshCcw, Loader2, Check, Plug } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { frappeApi } from '../../lib/api';

function StatusPill({ connected }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] text-[#4A4463]">
      <span className={`w-2 h-2 rounded-full ${connected ? 'bg-[#22c55e]' : 'bg-[#B8B3D4]'}`} />
      {connected ? 'Connected to Frappe HR' : 'Not connected — using mock data'}
    </span>
  );
}

function ConnectionRow({ siteUrl, connected, onCopy, onTest, testing }) {
  const text = siteUrl || 'No Frappe site configured yet';
  return (
    <div className="mt-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
      <div className="flex-1 inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-[#FAF8FF] border border-[#ECE6FA] overflow-hidden">
        <Lock size={14} className={`shrink-0 ${connected ? 'text-[#22c55e]' : 'text-[#5B4FE8]'}`} />
        <span className="text-[13px] font-mono text-[#1A1533] tracking-wide truncate" title={siteUrl || ''}>
          {text}
        </span>
        <button
          onClick={onCopy}
          disabled={!siteUrl}
          className="ml-auto w-7 h-7 rounded hover:bg-[#F1ECFE] text-[#5B4FE8] flex items-center justify-center shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
          aria-label="Copy site URL"
        >
          <Copy size={13} />
        </button>
      </div>
      <button
        onClick={onTest}
        disabled={testing}
        className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#F1ECFE] text-[#5B4FE8] text-[13px] font-semibold hover:bg-[#E9E3FB] disabled:opacity-60"
      >
        {testing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
        Re-test connection
      </button>
    </div>
  );
}

function FrappeForm({ draft, setDraft, onSave, saving, connected }) {
  return (
    <div className="mt-6 rounded-xl border border-[#ECE6FA] bg-white p-4">
      <div className="flex items-center gap-2">
        <Plug size={14} className="text-[#5B4FE8]" />
        <p className="text-[13.5px] font-semibold text-[#1A1533]">Frappe HR connection</p>
      </div>
      <p className="text-[12px] text-[#4A4463] mt-1">
        Enter your Frappe site URL and API credentials to sync jobs from Frappe HR. When credentials are missing or invalid we fall back to mock jobs.
      </p>
      <div className="mt-3 space-y-2">
        <input
          placeholder="Frappe site URL (e.g. https://your-site.frappe.cloud)"
          value={draft.site_url}
          onChange={(e) => setDraft({ ...draft, site_url: e.target.value })}
          className="w-full px-3 py-2.5 rounded-lg border border-[#ECE6FA] bg-white text-[13px] outline-none focus:ring-2 focus:ring-[#5B4FE8]/30"
        />
        <input
          placeholder={connected ? 'API key (leave blank to keep saved key)' : 'API key'}
          value={draft.api_key}
          onChange={(e) => setDraft({ ...draft, api_key: e.target.value })}
          className="w-full px-3 py-2.5 rounded-lg border border-[#ECE6FA] bg-white text-[13px] outline-none focus:ring-2 focus:ring-[#5B4FE8]/30"
        />
        <input
          placeholder={connected ? 'API secret (leave blank to keep saved secret)' : 'API secret'}
          type="password"
          value={draft.api_secret}
          onChange={(e) => setDraft({ ...draft, api_secret: e.target.value })}
          className="w-full px-3 py-2.5 rounded-lg border border-[#ECE6FA] bg-white text-[13px] outline-none focus:ring-2 focus:ring-[#5B4FE8]/30"
        />
      </div>
      <button
        onClick={onSave}
        disabled={saving}
        className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#5B4FE8] text-white text-[13px] font-semibold hover:bg-[#4A3FD9] disabled:opacity-60"
      >
        {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
        Save & test
      </button>
      <p className="mt-2 text-[11.5px] text-[#8A83A5]">
        Your credentials are stored securely on the server and used only to call your Frappe HR site.
      </p>
    </div>
  );
}

export default function APIAccessTab() {
  const { toast } = useToast();
  const [status, setStatus] = useState({ connected: false, site_url: '' });
  const [draft, setDraft] = useState({ site_url: '', api_key: '', api_secret: '' });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // Fetch status on mount + pre-fill site_url so the user can edit, not retype.
  useEffect(() => {
    let active = true;
    frappeApi
      .status()
      .then((s) => {
        if (!active) return;
        setStatus(s);
        setDraft((d) => ({ ...d, site_url: d.site_url || s.site_url || '' }));
      })
      .catch(() => {
        // Network / backend errors are non-fatal here — leave defaults
      });
    return () => {
      active = false;
    };
  }, []);

  const save = async () => {
    if (!draft.site_url.trim()) {
      toast({ title: 'Site URL required', description: 'Please enter your Frappe site URL.' });
      return;
    }
    setSaving(true);
    try {
      // If user left api_key/api_secret blank but a key was saved before,
      // we still send blanks (backend overwrites). Force re-entry to update.
      const res = await frappeApi.saveConfig({
        site_url: draft.site_url.trim(),
        api_key: draft.api_key.trim(),
        api_secret: draft.api_secret,
      });
      setStatus(res);
      toast({
        title: res.connected ? 'Connected' : 'Saved',
        description: res.connected
          ? 'Frappe HR is now connected. Jobs will sync from your site.'
          : 'Credentials saved, but the site is not yet reachable. Double-check key, secret, and URL.',
      });
      // Clear secrets from the form once saved (don't keep them on the page).
      setDraft((d) => ({ ...d, api_key: '', api_secret: '' }));
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Network error';
      toast({ title: 'Save failed', description: msg });
    } finally {
      setSaving(false);
    }
  };

  const retest = async () => {
    setTesting(true);
    try {
      const s = await frappeApi.status();
      setStatus(s);
      toast({
        title: s.connected ? 'Connection OK' : 'Not connected',
        description: s.connected
          ? `Live on ${s.site_url}`
          : 'No valid Frappe credentials saved yet.',
      });
    } catch (e) {
      toast({ title: 'Test failed', description: e?.message || 'Network error' });
    } finally {
      setTesting(false);
    }
  };

  const copySite = () => {
    if (!status.site_url) return;
    navigator.clipboard?.writeText(status.site_url);
    toast({ title: 'Copied', description: status.site_url });
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-[18px] font-bold text-[#1A1533]">API</h2>
        <StatusPill connected={status.connected} />
      </div>
      <ConnectionRow
        siteUrl={status.site_url}
        connected={status.connected}
        onCopy={copySite}
        onTest={retest}
        testing={testing}
      />
      <a
        href="https://frappeframework.com/docs/v14/user/en/api"
        target="_blank"
        rel="noreferrer"
        className="mt-3 w-full flex items-center justify-between px-4 py-3 rounded-xl border border-[#ECE6FA] hover:bg-[#FAF8FF]"
      >
        <span className="text-[13px] font-semibold text-[#1A1533]">See Frappe API docs</span>
        <ExternalLink size={14} className="text-[#5B4FE8]" />
      </a>

      <FrappeForm
        draft={draft}
        setDraft={setDraft}
        onSave={save}
        saving={saving}
        connected={status.connected}
      />
    </div>
  );
}
